import { CheckCircle2 } from "lucide-react";
import { useState } from "react";
import Modal from "@/components/ui/Modal";
import Button from "@/components/ui/Button";
import Textarea from "@/components/ui/Textarea";
import type { KycRecord } from "@/types/kyc";
import { formatINR } from "@/pages/admin/data";
import { KYC_FEE } from "@/pages/retailer/wallet/constants";

interface ApproveModalProps {
  open: boolean;
  onClose: () => void;
  record: KycRecord | null;
  onConfirm: (remark: string) => void;
}

export default function ApproveModal({ open, onClose, record, onConfirm }: ApproveModalProps) {
  const [remark, setRemark] = useState("");
  const [submitting, setSubmitting] = useState(false);

  function reset() {
    setRemark("");
    setSubmitting(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  function handleConfirm() {
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      onConfirm(remark.trim());
      reset();
    }, 900);
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Approve this KYC application?"
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2.5 border-t border-line px-5 py-4">
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} loading={submitting}>
            Approve KYC
          </Button>
        </div>
      }
    >
      {record && (
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3 rounded-[10px] border border-[#A7F3D0] bg-[#ECFDF5] p-4">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-[#059669]">
              <CheckCircle2 className="h-5 w-5" />
            </span>
            <p className="text-[13px] text-ink-secondary">
              The retailer will immediately be able to create mandates. The {formatINR(KYC_FEE)} verification fee
              already deducted will be retained.
            </p>
          </div>

          <div className="flex flex-col divide-y divide-[#F1F5F9]">
            <div className="flex items-center justify-between gap-4 py-2.5">
              <span className="text-[13px] text-ink-secondary">Business Name</span>
              <span className="text-sm font-medium text-ink">{record.legalBusinessName}</span>
            </div>
            <div className="flex items-center justify-between gap-4 py-2.5">
              <span className="text-[13px] text-ink-secondary">Owner Name</span>
              <span className="text-sm font-medium text-ink">
                {record.ownerFullName || record.retailerName || record.retailerId}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4 py-2.5">
              <span className="text-[13px] text-ink-secondary">Retailer ID</span>
              <span className="text-sm font-medium text-ink">{record.retailerId}</span>
            </div>
          </div>

          <Textarea
            label="Internal Remark"
            rows={3}
            maxLength={200}
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            placeholder="Add an internal note (optional)"
          />
          <p className="-mt-3 text-right text-xs text-ink-muted">{remark.length}/200</p>
        </div>
      )}
    </Modal>
  );
}